import React, { useState, useEffect, useCallback } from 'react';
import WalterLoader from '../shared/WalterLoader';
import { FaTrash, FaExclamationCircle } from 'react-icons/fa';
import { useAppMetadata } from '../shared/useAppMetadata';
import { slugify } from '../../utils/slugify';
import '../../styles/Admin.css';

const API_URL = process.env.REACT_APP_API_URL;

const putJson = (path, body) => fetch(`${API_URL}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
});

/* ─────────────────────────────────────────────
   Écran d'admin pour la liste des types de bâtiment (app_metadata,
   type="building_types"), jusqu'ici modifiable uniquement via
   migrate_building_types.py. Le `value` (slug stocké sur
   buildings.type_building) n'est jamais modifiable après création — seul le
   libellé peut être renommé — pour ne jamais casser un bâtiment existant.
   Suppression bloquée tant qu'au moins un bâtiment utilise le type. Pas de
   réordonnancement manuel : la liste suit l'ordre de création.
───────────────────────────────────────────── */
const BuildingTypes = () => {
    const { data: buildingTypes, loading, reload } = useAppMetadata('building_types');
    const [counts, setCounts] = useState({});
    const [newLabel, setNewLabel] = useState('');
    const [error, setError] = useState(null);

    const loadCounts = useCallback(() => {
        fetch(`${API_URL}/get_building_type_counts`)
            .then((r) => r.json())
            .then((json) => setCounts(json && typeof json === 'object' ? json : {}))
            .catch(() => setCounts({}));
    }, []);

    useEffect(() => { loadCounts(); }, [loadCounts]);

    const saveList = async (nextList) => {
        setError(null);
        const res = await putJson('/set_app_metadata/building_types', nextList);
        if (!res.ok) {
            setError("Erreur lors de l'enregistrement.");
            return;
        }
        await reload();
        loadCounts();
    };

    const addType = () => {
        const label = newLabel.trim();
        if (!label) return;
        const value = slugify(label);
        if (buildingTypes.some((t) => t.value === value)) {
            setError('Ce type existe déjà.');
            return;
        }
        saveList([...buildingTypes, { value, label }]);
        setNewLabel('');
    };

    const renameType = (value, label) => {
        if (!label.trim()) return;
        saveList(buildingTypes.map((t) => (t.value === value ? { ...t, label: label.trim() } : t)));
    };

    const deleteType = (value) => {
        if (counts[value]) return;
        if (!window.confirm('Supprimer ce type de bâtiment ?')) return;
        saveList(buildingTypes.filter((t) => t.value !== value));
    };

    if (loading) {
        return (
            <div className="centered-spinner-dashboard">
                <WalterLoader />
            </div>
        );
    }

    return (
        <div className="admin-page-content admin-page-content--narrow-top">
            {error && (
                <div className="admin-error-banner"><FaExclamationCircle />{error}</div>
            )}

            <div className="admin-card">
                <div className="admin-card-header">
                    <span className="admin-card-title">
                        <span className="admin-card-title-bar" />
                        Types disponibles
                    </span>
                    <span className="admin-count-badge">{buildingTypes.length}</span>
                </div>

                {buildingTypes.length === 0 && (
                    <div className="admin-empty-state">Aucun type de bâtiment pour l'instant.</div>
                )}

                {buildingTypes.map((type) => {
                    const count = counts[type.value] || 0;
                    return (
                        <div key={type.value} className="admin-row">
                            <input
                                type="text" className="admin-row-input" defaultValue={type.label}
                                onBlur={(e) => e.target.value !== type.label && renameType(type.value, e.target.value)}
                            />
                            <span className="admin-slug-tag">{type.value}</span>
                            <div className="admin-row-meta">
                                <span className="admin-usage-count">
                                    {count} bâtiment{count > 1 ? 's' : ''}
                                </span>
                                <button type="button" className="admin-icon-btn admin-icon-btn--danger"
                                    onClick={() => deleteType(type.value)} disabled={count > 0}
                                    title={count > 0 ? 'Reclasser les bâtiments concernés avant de supprimer ce type' : 'Supprimer'}>
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                    );
                })}

                <div className="admin-add-row">
                    <input
                        type="text" placeholder="Nouveau type de bâtiment"
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addType()}
                    />
                    <button type="button" className="admin-add-btn" onClick={addType} disabled={!newLabel.trim()}>
                        + Ajouter
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BuildingTypes;
